import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import TrackerShell from "../../components/tracker/TrackerShell";
import {
  TrackerContainer,
  TrackerFilters,
  filterByBaseShop,
} from "../../components/tracker-ui";
import "../MyTeam/shared-tracker-styles.css";
import { db, auth } from "../../firebase";
import {
  collection,
  onSnapshot,
  setDoc,
  doc,
  serverTimestamp,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  getDoc,
  updateDoc,
} from "firebase/firestore";

import UserProfileCard from "../../components/profile/UserProfileCard";
import useProfileModalRoute from "../../components/profile/useProfileModalRoute";
import { useUIVersion } from "../../contexts/UIVersionContext";
import { useAuth } from "../../hooks/useAuth";
import { ACCOUNT_TYPES, useAccountType } from "../../hooks/useAccountType";
import { useDebounce } from "../../hooks/useDebounce";
import { IconUsers } from "@tabler/icons-react";

// ✅ Import the updated safe sync service
import {
  toggleFrom4x4,
  toggleLicensed,
  updateField,
  patchSingleField,
  SOURCE,
} from "./services/trackerSync";

// ✅ Import notes service
import {
  addNote,
  deleteNote as deleteNoteService,
  subscribeToProspectNotes,
} from "../../services/notesService";

/* =========================
   Firestore Constants
   ========================= */
const TRACKERS_COLLECTION = "trackers"; // Single source of truth
const LICENSING_COLLECTION = "licensing"; // New licensing collection

/* =========================
   Default fourByFour structure - NEW FIELD NAMES per requirements
   ========================= */
const DEFAULT_4X4 = {
  // Basic info
  recruiter: "",
  leader: "",
  email: "",
  phone: "",
  level: "",
  code: "",
  date: null,

  // Recruit milestones - NEW field names
  finish1stRecruit: false,
  finish2ndRecruit: false,
  finish3rdRecruit: false,

  // Savings milestones - NEW field names with amounts
  personalSavings: false,
  personalSavingsAmount: null,
  finish2ndSavings: false,
  finish2ndSavingsAmount: null,
  finish3rdSavings: false,
  finish3rdSavingsAmount: null,
  finish4thSavings: false,
  finish4thSavingsAmount: null,

  // Big Event (can be toggled, also set by Associate.selfImprove rule)
  bigEvent: false,

  // Register for Convention (UI parity, not driven from 4x4)
  registerForConvention: false,

  // Mirrored from Licensing
  sirconNiprDate: null,
  passExamDate: null,
  licensed: false,

  // Timestamps
  createdAt: null,
  updatedAt: null,
};

/* =========================
   Hierarchy Filtering
   ========================= */
// Build the list of userIds a user can view based on plan and hierarchy
const DOWNLINE_PLANS = new Set([
  ACCOUNT_TYPES.LEADER,
  ACCOUNT_TYPES.BROKER,
  ACCOUNT_TYPES.SENIOR_BROKER,
  ACCOUNT_TYPES.ADMIN,
]);

function buildAccessibleUsers(plan, allUsers, currentUserId) {
  if (!currentUserId) return new Set();

  // New Agent / Agent: only their own data
  if (!DOWNLINE_PLANS.has(plan)) {
    return new Set([currentUserId]);
  }

  // Admin: can monitor everything
  if (plan === ACCOUNT_TYPES.ADMIN) {
    const all = new Set([currentUserId]);
    allUsers.forEach((u) => all.add(u.id));
    return all;
  }

  // Leaders/Brokers/Senior Brokers: their users + full downline (via recruiterId chain)
  const childrenMap = new Map();
  allUsers.forEach((u) => {
    if (!u?.recruiterId) return;
    if (!childrenMap.has(u.recruiterId)) childrenMap.set(u.recruiterId, []);
    childrenMap.get(u.recruiterId).push(u.id);
  });

  const allowed = new Set([currentUserId]);
  const stack = [currentUserId];
  while (stack.length) {
    const node = stack.pop();
    const kids = childrenMap.get(node) || [];
    kids.forEach((kid) => {
      if (!allowed.has(kid)) {
        allowed.add(kid);
        stack.push(kid);
      }
    });
  }

  return allowed;
}

/* =========================
   Helper to ensure fourByFour exists (FIXED - doesn't overwrite progress)
   ========================= */
const ensureFourByFourExists = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, TRACKERS_COLLECTION, userId));

    if (userDoc.exists()) {
      const userData = userDoc.data();
      const existingFourByFour = userData.fourByFour || {};

      // Only merge missing fields, don't overwrite existing progress
      const mergedFourByFour = { ...DEFAULT_4X4, ...existingFourByFour };

      // Deep merge for nested objects
      if (existingFourByFour.recruits) {
        mergedFourByFour.recruits = {
          ...DEFAULT_4X4.recruits,
          ...existingFourByFour.recruits,
        };
      }
      if (existingFourByFour.milestones) {
        mergedFourByFour.milestones = {
          ...DEFAULT_4X4.milestones,
          ...existingFourByFour.milestones,
        };
      }

      // Only update if there are actually missing fields
      if (
        JSON.stringify(mergedFourByFour) !== JSON.stringify(existingFourByFour)
      ) {
        await setDoc(
          doc(db, TRACKERS_COLLECTION, userId),
          {
            fourByFour: mergedFourByFour,
            updatedAt: serverTimestamp(),
            lastUpdatedBy: SOURCE.FOUR_BY_FOUR,
          },
          { merge: true }
        );
      }
    } else {
      // Create new document with default structure
      await setDoc(
        doc(db, TRACKERS_COLLECTION, userId),
        {
          fourByFour: DEFAULT_4X4,
          updatedAt: serverTimestamp(),
          lastUpdatedBy: SOURCE.FOUR_BY_FOUR,
        },
        { merge: true }
      );
    }
  } catch (error) {
    console.error("Error ensuring fourByFour exists:", error);
    throw error;
  }
};

/* =========================
   Columns - NEW FIELD NAMES per requirements
   ========================= */
const COLS = [
  { key: "index", label: "#", width: 40, showLock: false },
  { key: "name", label: "Name", width: 200, sortable: true },
  { key: "recruiter", label: "Recruiter", width: 200, sortable: true },
  { key: "leader", label: "Leader", width: 200, sortable: true },

  // Philosophy Section
  {
    key: "fourByFour.finish1stRecruit",
    label: "Finish 1st Recruit",
    type: "check",
    width: 100,
    sortable: true
  },
  {
    key: "fourByFour.personalSavings",
    label: "Personal Savings",
    type: "check",
    savings: true,
    savingsKey: "fourByFour.personalSavings",
    amountKey: "fourByFour.personalSavingsAmount",
    width: 140,
    sortable: true
  },
  {
    key: "fourByFour.bigEvent",
    label: "Big Event",
    type: "check",
    width: 100,
    sortable: true
  },

  // System Section - Recruits
  {
    key: "fourByFour.finish2ndRecruit",
    label: "Finish 2nd Recruit",
    shortLabel: "2nd Rec",
    type: "check",
    width: 120,
    sortable: true
  },
  {
    key: "fourByFour.finish3rdRecruit",
    label: "Finish 3rd Recruit",
    shortLabel: "3rd Rec",
    type: "check",
    width: 120,
    sortable: true
  },

  // System Section - Savings with amounts
  {
    key: "fourByFour.finish2ndSavings",
    label: "Finish 2nd Savings",
    shortLabel: "2nd Sav",
    type: "check",
    savings: true,
    savingsKey: "fourByFour.finish2ndSavings",
    amountKey: "fourByFour.finish2ndSavingsAmount",
    width: 150,
    sortable: true
  },
  {
    key: "fourByFour.finish3rdSavings",
    label: "Finish 3rd Savings",
    shortLabel: "3rd Sav",
    type: "check",
    savings: true,
    savingsKey: "fourByFour.finish3rdSavings",
    amountKey: "fourByFour.finish3rdSavingsAmount",
    width: 150,
    sortable: true
  },
  {
    key: "fourByFour.finish4thSavings",
    label: "Finish 4th Savings",
    shortLabel: "4th Sav",
    type: "check",
    savings: true,
    savingsKey: "fourByFour.finish4thSavings",
    amountKey: "fourByFour.finish4thSavingsAmount",
    width: 150,
    sortable: true
  },

  // Licensing - Mirrored fields
  {
    key: "fourByFour.passExamDate",
    label: "Pass Exam",
    shortLabel: "Exam",
    type: "date",
    width: 120,
    readOnly: true, // Mirrored from Licensing
    sortable: true
  },
  {
    key: "fourByFour.sirconNiprDate",
    label: "Sircon/NIPR",
    shortLabel: "Sircon",
    type: "date",
    width: 120,
    readOnly: true, // Mirrored from Licensing
    sortable: true
  },
  {
    key: "fourByFour.licensed",
    label: "Licensed",
    type: "check",
    readOnly: true, // Mirrored from Licensing
    width: 100,
    sortable: true
  },

  { key: "fourByFour.notes", label: "Notes", type: "notesInline", width: 320 },
];

// Divider columns - updated for new field names
const THICK_DIVIDER_KEYS = [
  "fourByFour.finish2ndRecruit",
  "fourByFour.finish2ndSavings",
  "fourByFour.passExamDate",
];

/* =========================
   Helpers
   ========================= */
function displayName(row) {
  const combo = [row.firstName, row.lastName].filter(Boolean).join(" ");
  return row.name || row.fullName || combo || "—";
}

function initialsFrom(name) {
  const n = (name || "").trim();
  if (!n) return "?";
  const p = n.split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase();
}

function toMillis(tsLike) {
  if (!tsLike) return 0;
  if (typeof tsLike?.toDate === "function") return tsLike.toDate().getTime();
  const t = new Date(tsLike).getTime();
  return isNaN(t) ? 0 : t;
}

function formatDateShort(isoOrTs) {
  if (!isoOrTs) return "";
  const d =
    typeof isoOrTs?.toDate === "function"
      ? isoOrTs.toDate()
      : new Date(isoOrTs);
  if (isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

/* ===== COUNTDOWN (30-day window from AMA date) ===== */
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
function getCountdownFromAma(row) {
  const amaIso = row.date || row.amaDate;
  if (!amaIso)
    return { hasAma: false, daysLeft: null, endDateISO: null, label: "—" };
  const today = startOfDay(new Date());
  const ama = startOfDay(new Date(amaIso));
  if (isNaN(ama.getTime()))
    return { hasAma: false, daysLeft: null, endDateISO: null, label: "—" };
  const endDate = addDays(ama, 30);
  const diffMs = endDate - today;
  const daysLeft = Math.ceil(diffMs / MS_PER_DAY);
  return {
    hasAma: true,
    daysLeft,
    endDateISO: endDate.toISOString(),
    label: daysLeft > 0 ? `${daysLeft}d` : `0d`,
  };
}

/* Avatar */
function AvatarSquare({ row, size = 30, onClick }) {
  const url = row.photoURL || row.avatarUrl || row.photo;
  const txt = initialsFrom(displayName(row));
  return url ? (
    <img
      src={url}
      alt={displayName(row)}
      className="avatar-square"
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        cursor: "pointer",
        objectFit: "cover",
      }}
      onClick={onClick}
    />
  ) : (
    <div
      className="avatar-fallback"
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        cursor: "pointer",
        background: "rgba(255,255,255,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        color: "#f3d67a",
        fontSize: "12px",
      }}
      onClick={onClick}
      title="No profile photo"
    >
      {txt}
    </div>
  );
}

/* =========================
   Gold Header Rows (updated for "SELF IMPROVE")
   ========================= */
function GoldHeaderRows({ columns, colWidths, startLeft = 0 }) {
  const totalWidth = useMemo(
    () =>
      columns.reduce((s, c) => s + (colWidths[c.key] ?? (c.width || 140)), 0),
    [columns, colWidths]
  );
  const columnPositions = useMemo(() => {
    const positions = [];
    let currentLeft = 0;
    columns.forEach((col, index) => {
      positions.push({
        key: col.key,
        left: currentLeft,
        width: colWidths[col.key] ?? (col.width || 140),
        index,
      });
      currentLeft += colWidths[col.key] ?? (col.width || 140);
    });
    return positions;
  }, [columns, colWidths]);

  const headerSections = [
    [
      {
        from: "fourByFour.finish1stRecruit",
        to: "fourByFour.bigEvent",
        label: "PHILOSOPHY",
        hasLogo: true,
        logoUrl:
          "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/missionpin.png?alt=media&token=c6534897-de60-4981-912c-4b73802cc918",
      },
      {
        from: "fourByFour.finish2ndRecruit",
        to: "fourByFour.licensed",
        label: "SYSTEM",
        hasLogo: true,
        logoUrl:
          "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/4x4logo.png?alt=media&token=97a5ffa1-894f-4224-b009-dffbfc73b89e",
      },
    ],
    [
      {
        from: "fourByFour.finish1stRecruit",
        to: "fourByFour.finish1stRecruit",
        lines: ["MULTI", "HANDED"],
      },
      {
        from: "fourByFour.personalSavings",
        to: "fourByFour.personalSavings",
        lines: ["10%", "3 RULES", "3 GOALS"],
      },
      {
        from: "fourByFour.bigEvent",
        to: "fourByFour.bigEvent",
        lines: ["BIG", "EVENT"],
      },
      {
        from: "fourByFour.finish2ndRecruit",
        to: "fourByFour.licensed",
        lines: ["4X4"],
      },
    ],
  ];

  const calculateSectionPosition = (fromKey, toKey) => {
    const fromIndex = columns.findIndex((col) => col.key === fromKey);
    const toIndex = columns.findIndex((col) => col.key === toKey);
    if (fromIndex === -1 || toIndex === -1) return null;
    const left = columnPositions[fromIndex]?.left || 0;
    const right =
      columnPositions[toIndex]?.left + columnPositions[toIndex]?.width;
    return { left, width: right - left };
  };

  return (
    <>
      {headerSections.map((rowSections, rowIndex) => {
        const topOffset = rowIndex === 0 ? 0 : 56;
        const computedTop = `calc(var(--filters-height, 0px) + ${topOffset}px)`;
        return (
          <div
            key={rowIndex}
            className="gold-header-row"
            style={{
              position: "sticky",
              top: computedTop,
              width: totalWidth,
              height: rowIndex === 0 ? 56 : 70,
              background:
                "#252830",
              borderBottom: "1px solid rgba(230,200,90,0.4)",
              display: "flex",
              zIndex: rowIndex === 0 ? 19 : 18,
              pointerEvents: "none",
              marginLeft: startLeft,
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
              willChange: "transform",
            }}
          >
            {rowSections.map((section, sectionIndex) => {
              const position = calculateSectionPosition(
                section.from,
                section.to
              );
              if (!position) return null;
              return (
                <div
                  key={sectionIndex}
                  className="gold-section"
                  style={{
                    position: "absolute",
                    left: position.left,
                    width: position.width,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderLeft:
                      sectionIndex > 0
                        ? "1px solid rgba(230,200,90,0.3)"
                        : "none",
                    borderRight:
                      sectionIndex < rowSections.length - 1
                        ? "1px solid rgba(230,200,90,0.3)"
                        : "none",
                    zIndex: 2,
                  }}
                >
                  {rowIndex === 0 ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: "0.35px",
                        fontSize: "16px",
                        color: "#f3d67a",
                      }}
                    >
                      <span>{section.label}</span>
                      {section.hasLogo && (
                        <img
                          src={section.logoUrl}
                          alt={section.label}
                          style={{
                            height: "38px",
                            objectFit: "contain",
                            filter: "drop-shadow(0 1px 2px rgba(0,0,0,.25))",
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                        fontWeight: 900,
                        textTransform: "uppercase",
                        fontSize: section.lines.length > 1 ? "15px" : "16px",
                        color: "#f3d67a",
                        lineHeight: "1.05",
                        textAlign: "center",
                        wordWrap: "break-word",
                        maxWidth: "100%",
                        padding: "0 4px",
                        boxSizing: "border-box",
                      }}
                    >
                      {section.lines.map((line, lineIndex) => (
                        <span
                          key={lineIndex}
                          style={{
                            display: "block",
                            width: "100%",
                          }}
                        >
                          {line}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}

/* =========================
   Grid (updated with new header layout and date fields)
   ========================= */
function ResizableGrid({
  columns,
  rows,
  loading,
  emptyText,
  renderCell,
  rowKey,
  lockableLeftCount = 4,
  initialLockedKeys = ["index", "name", "recruiter", "leader"],
  sortKey,
  sortDir,
  onSort,
  renderHeaderSearch,
  onScroll,
  loadingMore,
}) {
  const storageKey = "tracker-4x4-columns";
  const [colWidths, setColWidths] = useState(() =>
    Object.fromEntries(columns.map((c) => [c.key, c.width || 140]))
  );


  const defaultLocked = initialLockedKeys?.length
    ? new Set(initialLockedKeys)
    : new Set(columns.slice(0, lockableLeftCount).map((c) => c.key));
  const [lockedKeys, setLockedKeys] = useState(defaultLocked);

  const lockedColumns = useMemo(
    () => columns.filter((c) => lockedKeys.has(c.key)),
    [columns, lockedKeys]
  );
  const scrollableColumns = useMemo(
    () => columns.filter((c) => !lockedKeys.has(c.key)),
    [columns, lockedKeys]
  );
  const lockedBlockWidth = useMemo(
    () =>
      lockedColumns.reduce(
        (sum, col) => sum + (colWidths[col.key] ?? (col.width || 140)),
        0
      ),
    [lockedColumns, colWidths]
  );
  const scrollColumnOffsets = useMemo(() => {
    let acc = 0;
    const offsets = {};
    scrollableColumns.forEach((col) => {
      offsets[col.key] = acc;
      acc += colWidths[col.key] ?? (col.width || 140);
    });
    return offsets;
  }, [scrollableColumns, colWidths]);

  const dragRef = useRef(null);

  // Refs for scroll synchronization
  const lockedSectionRef = useRef(null);
  const scrollSectionRef = useRef(null);

  // Sync scrolling between locked and scrollable sections
  useEffect(() => {
    const lockedEl = lockedSectionRef.current;
    const scrollEl = scrollSectionRef.current;
    if (!lockedEl || !scrollEl) return;

    let isSyncing = false; // Flag to prevent infinite loop

    const handleLockedScroll = () => {
      if (!isSyncing) {
        isSyncing = true;
        scrollEl.scrollTop = lockedEl.scrollTop;
        requestAnimationFrame(() => {
          isSyncing = false;
        });
      }
    };

    const handleScrollScroll = () => {
      if (!isSyncing) {
        isSyncing = true;
        lockedEl.scrollTop = scrollEl.scrollTop;
        requestAnimationFrame(() => {
          isSyncing = false;
        });
      }
    };

    lockedEl.addEventListener('scroll', handleLockedScroll, { passive: true });
    scrollEl.addEventListener('scroll', handleScrollScroll, { passive: true });

    return () => {
      lockedEl.removeEventListener('scroll', handleLockedScroll);
      scrollEl.removeEventListener('scroll', handleScrollScroll);
    };
  }, [lockedColumns.length]); // Re-run when locked columns change

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const savedWidths = JSON.parse(saved);
        setColWidths((prev) => ({ ...prev, ...savedWidths }));
      } catch { }
    }
  }, []);
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const { key, startX, startW } = dragRef.current;
      const dx = e.clientX - startX;
      const next = Math.max(80, startW + dx);
      setColWidths((w) => ({ ...w, [key]: next }));
    };
    const onUp = () => {
      if (dragRef.current) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(colWidths));
        } catch { }
      }
      dragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [colWidths]);

  const startDrag = (key, e) => {
    dragRef.current = { key, startX: e.clientX, startW: colWidths[key] || 140 };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  const toggleLock = (key) =>
    setLockedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const renderLockedHeader = () => {
    // Gold headers: 56px + 70px = 126px, Scrollable header: 70px, Total: 196px
    const GOLD_HEADERS_HEIGHT = 126;
    const SCROLLABLE_HEADER_HEIGHT = 70;
    const LOCKED_HEADER_HEIGHT = GOLD_HEADERS_HEIGHT + SCROLLABLE_HEADER_HEIGHT; // 196px

    return (
      <div
        className="rg-row rg-header"
        style={{
          position: "sticky",
          top: "calc(var(--filters-height, 0px))",
          zIndex: 20,
          height: `${LOCKED_HEADER_HEIGHT}px`,
          display: "flex",
          marginTop: 0,
          background: "#252830",
        }}
      >
        {lockedColumns.map((c) => {
          const w = colWidths[c.key];
          const isSorted = c.sortable && c.key === sortKey;
          const sortLabel = isSorted ? (sortDir === "asc" ? "▲" : "▼") : "↕";
          const originalIndex = columns.findIndex((col) => col.key === c.key);
          return (
            <div
              key={c.key}
              className="rg-cell rg-head locked"
              style={{
                width: w,
                minWidth: w,
                maxWidth: w,
                height: `${LOCKED_HEADER_HEIGHT}px`,
                minHeight: `${LOCKED_HEADER_HEIGHT}px`,
                maxHeight: `${LOCKED_HEADER_HEIGHT}px`,
                backgroundColor: "#252830",
                border: "1px solid #3a3f4a",
                display: "flex",
                alignItems: "stretch",
                // Don't override padding - use CSS default to match scrollable headers
              }}
            >
              <div className="rg-head-inner" style={{ width: "100%" }}>
                <div className="rg-head-top">
                  <div className="rg-title-group">
                    <span className="rg-title" title={c.label}>{c.label}</span>
                  </div>
                  <div className="rg-actions-group">
                    {originalIndex < lockableLeftCount &&
                      c.showLock !== false && (
                        <button
                          type="button"
                          className="lock-btn on"
                          onClick={() => toggleLock(c.key)}
                          title="Unlock column"
                        >
                          🔒
                        </button>
                      )}
                    {c.sortable && (
                      <button
                        type="button"
                        className={`mini-btn sort-btn ${isSorted ? "on" : ""}`}
                        onClick={() => onSort && onSort(c.key)}
                        title={isSorted ? `Sorted ${sortDir}` : "Sort"}
                      >
                        {sortLabel}
                      </button>
                    )}
                  </div>
                </div>
                <div className="rg-head-search">
                  {renderHeaderSearch ? renderHeaderSearch(c) : null}
                </div>
              </div>
              <span
                className="rg-grip"
                onMouseDown={(e) => startDrag(c.key, e)}
                title="Drag to resize"
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderLockedRows = () =>
    rows.map((r, rowIdx) => (
      <div className="rg-row" key={rowKey ? rowKey(r) : rowIdx}>
        {lockedColumns.map((c) => {
          const w = colWidths[c.key];
          return (
            <div
              key={c.key}
              className="rg-cell locked"
              style={{ width: w, minWidth: w, maxWidth: w }}
            >
              {renderCell(r, c, rowIdx)}
            </div>
          );
        })}
      </div>
    ));

  const renderScrollableHeader = () => (
    <div
      className="rg-row rg-header"
      style={{
        position: "sticky",
        top: "calc(var(--filters-height, 0px) + 126px)",
        zIndex: 4,
      }}
    >
      {scrollableColumns.map((c) => {
        const w = colWidths[c.key];
        const isSorted = c.sortable && c.key === sortKey;
        const sortLabel = isSorted ? (sortDir === "asc" ? "▲" : "▼") : "↕";
        const originalIndex = columns.findIndex((col) => col.key === c.key);
        const isThickDivider = THICK_DIVIDER_KEYS.includes(c.key);
        return (
          <div
            className={`rg-cell rg-head ${isThickDivider ? "thick-left" : ""}`}
            key={c.key}
            style={{ width: w, minWidth: w, maxWidth: w }}
          >
            <div className="rg-head-inner">
              <div className="rg-head-top">
                <div className="rg-title-group">
                  {originalIndex < lockableLeftCount &&
                    c.showLock !== false && (
                      <button
                        type="button"
                        className={`lock-btn ${lockedKeys.has(c.key) ? "on" : ""
                          }`}
                        onClick={() => toggleLock(c.key)}
                        title={
                          lockedKeys.has(c.key)
                            ? "Unlock column"
                            : "Lock column"
                        }
                      >
                        {lockedKeys.has(c.key) ? "🔒" : "🔓"}
                      </button>
                    )}
                  <span className="rg-title" title={c.label}>{c.label}</span>
                </div>
                <div className="rg-actions-group">
                  {c.sortable && (
                    <button
                      type="button"
                      className={`mini-btn sort-btn ${isSorted ? "on" : ""}`}
                      onClick={() => onSort && onSort(c.key)}
                      title={isSorted ? `Sorted ${sortDir}` : "Sort"}
                    >
                      {sortLabel}
                    </button>
                  )}
                </div>
              </div>
              <div className="rg-head-search">
                {renderHeaderSearch ? renderHeaderSearch(c) : null}
              </div>
            </div>
            <span
              className="rg-grip"
              onMouseDown={(e) => startDrag(c.key, e)}
              title="Drag to resize"
            />
          </div>
        );
      })}
    </div>
  );

  const renderScrollableRows = () => {
    if (loading) return <div className="rg-empty">Loading…</div>;
    if (!rows.length)
      return <div className="rg-empty">{emptyText || "No data"}</div>;
    return (
      <>
        {rows.map((r, rowIdx) => (
          <div className="rg-row" key={rowKey ? rowKey(r) : rowIdx}>
            {scrollableColumns.map((c) => {
              const w = colWidths[c.key];
              const isThickDivider = THICK_DIVIDER_KEYS.includes(c.key);
              return (
                <div
                  className={`rg-cell ${isThickDivider ? "thick-left" : ""}`}
                  key={c.key}
                  style={{ width: w, minWidth: w, maxWidth: w }}
                >
                  {renderCell(r, c, rowIdx)}
                </div>
              );
            })}
          </div>
        ))}
        {loadingMore && rows.length > 0 && (
          <div className="rg-loading-more" style={{ padding: '20px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
            Loading more users...
          </div>
        )}
      </>
    );
  };

  return (
    <div className="rg-wrap">
      <div className="rg-layout">
        <div
          ref={lockedSectionRef}
          className="rg-locked-section"
          style={{ width: lockedBlockWidth, minWidth: lockedBlockWidth }}
        >
          {lockedColumns.length > 0 && renderLockedHeader()}
          {renderLockedRows()}
        </div>
        <div className="rg-scroll-section">
          <div 
            ref={scrollSectionRef} 
            className="rg-scroll"
            onScroll={(e) => {
              if (!onScroll) return;
              const { scrollTop, scrollHeight, clientHeight } = e.target;
              // Trigger load more when scrolled to bottom (with 200px threshold)
              if (scrollHeight - scrollTop - clientHeight < 200) {
                onScroll();
              }
            }}
          >
            {scrollableColumns.length > 0 && (
              <GoldHeaderRows
                columns={scrollableColumns}
                colWidths={colWidths}
                startLeft={0}
              />
            )}
            {scrollableColumns.length > 0 && renderScrollableHeader()}
            {Object.entries(scrollColumnOffsets).map(
              ([dividerKey, leftPosition]) =>
                THICK_DIVIDER_KEYS.includes(dividerKey) ? (
                  <div
                    key={dividerKey}
                    className="thick-vertical-divider"
                    style={{
                      position: "absolute",
                      left: leftPosition - 0.5,
                      top: "calc(var(--filters-height, 0px) + 126px)",
                      width: "1px",
                      bottom: 0,
                      background: "rgba(230,200,90,0.55)",
                      zIndex: 2,
                      pointerEvents: "none",
                    }}
                  />
                ) : null
            )}
            {renderScrollableRows()}
          </div>
        </div>
      </div>

      <style jsx>{`
        .rg-cell.rg-head {
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
        }
        
        .rg-head-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          height: 100%;
        }

        .rg-title-group {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          flex-grow: 1;
          min-width: 0;
        }

        .rg-title {
          pointer-events: none;
          color: #fff;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 12px;
          font-weight: 600;
          padding: 0 8px;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          width: 100%;
        }

        .rg-actions-group {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }

        .lock-btn,
        .sort-btn {
          background: rgba(255,255,255,.06);
          border: none;
          cursor: pointer;
          padding: 0;
          border-radius: 6px;
          font-size: 11px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          transition: all 0.2s ease;
          flex-shrink: 0;
          position: relative;
          z-index: 1;
          vertical-align: top;
          line-height: 1;
          margin: 0;
          align-self: center;
        }
        
        .lock-btn {
          margin-top: -8px;
        }

        .lock-btn:hover,
        .sort-btn:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .lock-btn.on,
        .sort-btn.on {
          background: rgba(66, 153, 225, 0.2);
        }

        /* Locked column data rows - match Dainara's row height exactly */
        .rg-row:not(.rg-header) .rg-cell.locked:nth-child(2),
        .rg-row:not(.rg-header) .rg-cell.locked:nth-child(3),
        .rg-row:not(.rg-header) .rg-cell.locked:nth-child(4) {
          min-height: 50px;
          height: 50px;
          max-height: 50px;
        }

        .rg-row:not(.rg-header) .rg-cell.locked:nth-child(2) .name-like-cell,
        .rg-row:not(.rg-header) .rg-cell.locked:nth-child(3) .cell-with-btn,
        .rg-row:not(.rg-header) .rg-cell.locked:nth-child(4) .cell-with-btn {
          height: 100%;
          min-height: 50px;
          max-height: 50px;
        }

        .rg-row:not(.rg-header) .rg-cell.locked:nth-child(2) .name-text-wrap {
          height: 100%;
          justify-content: center;
        }

        /* Lock scrollable header height to match unlocked state */
        .rg-scroll-section .rg-header {
          height: 70px !important;
          min-height: 70px !important;
          position: sticky !important;
          top: calc(var(--filters-height, 0px) + 126px) !important;
          z-index: 4 !important;
          background: #252830 !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
          will-change: transform !important;
        }

        .rg-scroll-section .rg-head {
          height: 70px !important;
          min-height: 70px !important;
        }

        /* Ensure GoldHeaderRows stay sticky */
        .gold-header-row {
          position: sticky !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
          will-change: transform !important;
        }

        /* Locked header cells - ensure they match scrollable header height */
        .rg-locked-section .rg-header .rg-cell.rg-head {
          height: 196px !important;
          min-height: 196px !important;
          max-height: 196px !important;
          padding: 8px 12px !important;
        }

        /* Ensure consistent row heights between locked and scrollable sections */
        .rg-locked-section .rg-row:not(.rg-header),
        .rg-scroll-section .rg-row:not(.rg-header) {
          min-height: 70px !important;
          height: 70px !important;
          display: flex !important;
          align-items: stretch !important;
        }

        /* Ensure all cells have consistent heights - match locked cell heights */
        .rg-locked-section .rg-row:not(.rg-header) .rg-cell,
        .rg-scroll-section .rg-row:not(.rg-header) .rg-cell {
          min-height: 70px !important;
          height: 70px !important;
          max-height: 70px !important;
          box-sizing: border-box !important;
        }

        /* Make check mark button rows thicker */
        .rg-cell .light-cell {
          min-height: 50px;
          height: 50px;
        }

        .rg-cell .light-cell .savings-input-container {
          height: 100%;
        }

        .rg-cell .light-cell .savings-input {
          height: 32px;
        }

        /* Hide scrollbar but keep functionality */
        .rg-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(255,215,0,.3) rgba(255,255,255,.05);
          -ms-overflow-style: auto;
          overflow-x: auto;
        }

        .rg-scroll::-webkit-scrollbar {
          height: 8px;
        }

        .rg-scroll::-webkit-scrollbar-track {
          background: rgba(255,255,255,.05);
        }

        .rg-scroll::-webkit-scrollbar-thumb {
          background: rgba(255,215,0,.3);
          border-radius: 4px;
        }

        .rg-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(255,215,0,.5);
        }
        
        .rg-scroll-section {
          overflow-x: auto;
          overflow-y: hidden;
        }
      `}</style>
    </div>
  );
}

/* =========================
   Helper functions for notes
   ========================= */
function uniqueId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `id_${Date.now()}_${Math.round(Math.random() * 1e9)}`;
}

function formatDateTimePretty(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = d
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    .toLowerCase();
  return `${date} at ${time}`;
}

function formatDateTimeISO(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} ${time}`;
}

/* =========================
   Helper to get nested field value (extended for non-boolean values)
   ========================= */
function getNestedValue(obj, path, fallback = false) {
  if (!obj) return fallback;
  const segments = path.split(".");
  let current = obj;
  for (const segment of segments) {
    if (current && typeof current === "object" && segment in current) {
      current = current[segment];
    } else {
      return fallback;
    }
  }
  return current !== undefined && current !== null ? current : fallback;
}

/* =========================
   Helper to set nested field value
   ========================= */
function setNestedValue(obj, path, value) {
  const segments = path.split(".");
  const newObj = { ...obj };
  let current = newObj;

  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i];
    if (!current[segment] || typeof current[segment] !== "object") {
      current[segment] = {};
    }
    current[segment] = { ...current[segment] }; // Ensure immutability
    current = current[segment];
  }

  current[segments[segments.length - 1]] = value;
  return newObj;
}

/* =========================
   Main Component
   ========================= */
export default function Tracker4x4() {
  const { isFeatureEnabled } = useUIVersion();
  const modernTrackersEnabled = isFeatureEnabled("modernTrackers");
  const { user } = useAuth();
  const { plan: accountPlan } = useAccountType();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  // Hierarchy filtering
  const [allUsers, setAllUsers] = useState([]);
  const [allowedUserIds, setAllowedUserIds] = useState(new Set());

  // Filters/pagination (UI-level)
  const [dateRange, setDateRange] = useState(null);
  const [baseShopFilter, setBaseShopFilter] = useState("all");
  const [nameQ, setNameQ] = useState("");
  const [recruiterQ, setRecruiterQ] = useState("");
  const [leaderQ, setLeaderQ] = useState("");

  // ✅ Debounce search filters to avoid immediate query firing
  const debouncedNameQ = useDebounce(nameQ, 500);
  const debouncedRecruiterQ = useDebounce(recruiterQ, 500);
  const debouncedLeaderQ = useDebounce(leaderQ, 500);

  const [notesOpenFor, setNotesOpenFor] = useState(null);
  const [savingsInputActive, setSavingsInputActive] = useState(null); // { userId, savingsKey }
  const [noteDrafts, setNoteDrafts] = useState({});
  const [focusedNoteInputId, setFocusedNoteInputId] = useState(null);
  const [noteDeletingId, setNoteDeletingId] = useState(null);
  const [notesByEmail, setNotesByEmail] = useState({}); // All notes grouped by emailId

  // cursor for Firestore paging
  const pageStateRef = useRef({
    nextCursor: null,
    pageSize: 50,
    baseQueryKey: "default",
  });

  // live listeners for trackers docs
  const trackerUnsubsRef = useRef(new Map()); // userId -> unsub
  const licensingUnsubsRef = useRef(new Map()); // userId -> unsub for licensing

  const {
    id: profileId,
    openWithId: openProfile,
    close: closeProfile,
  } = useProfileModalRoute();
  const [profileData, setProfileData] = useState(null);

  // Attach licensing listener for a user
  const attachLicensingListener = useCallback((userId) => {
    if (licensingUnsubsRef.current.has(userId)) return;

    const unsub = onSnapshot(
      doc(db, LICENSING_COLLECTION, userId),
      (docSnapshot) => {
        if (!docSnapshot.exists()) return;

        const licensingData = docSnapshot.data();

        setRows((prev) =>
          prev.map((row) =>
            row.id === userId
              ? {
                ...row,
                licensing: licensingData,
                // Sync the boolean fields for compatibility
                fourByFour: {
                  ...row.fourByFour,
                  passExam: !!licensingData.testResultDate,
                  application: !!licensingData.sirconNiprDate,
                  // ✅ Sync getLicensed from licensing tracker
                  milestones: {
                    ...row.fourByFour.milestones,
                    getLicensed: !!licensingData.licenseActive,
                  },
                },
              }
              : row
          )
        );
      }
    );

    licensingUnsubsRef.current.set(userId, unsub);
  }, []);

  // Fetch all users to build hierarchy
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      const users = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllUsers(users);
    });
    return () => unsub();
  }, []);

  // Compute which users can be viewed based on account type and hierarchy
  useEffect(() => {
    if (!user) {
      setAllowedUserIds(new Set());
      return;
    }
    const allowed = buildAccessibleUsers(
      accountPlan,
      allUsers,
      user.uid
    );
    setAllowedUserIds(allowed);
  }, [user, accountPlan, allUsers]);

  // Initial page: query trackers collection ordered by fourByFour.createdAt desc
  useEffect(() => {
    let cancelled = false;

    async function loadInitial() {
      setLoading(true);
      setError("");

      try {
        // ✅ When search filters are active, load ALL data (no limit)
        const hasSearchFilters =
          debouncedNameQ.trim() ||
          debouncedRecruiterQ.trim() ||
          debouncedLeaderQ.trim();

        const mapTrackerToRow = (tracker) => {
          const fourByFour = tracker.fourByFour || {};

          return {
            id: tracker.id,
            name: tracker.name || displayName(tracker),
            firstName: tracker.firstName,
            lastName: tracker.lastName,
            email: tracker.email,
            phone: tracker.phone,
            photoURL: tracker.photoURL,
            leader: tracker.leader || "—",
            recruiter: fourByFour.recruiter || "—",
            code: tracker.code,
            date: tracker.date,
            photo: tracker.photoURL,
            baseShopId: tracker.baseShopId || null,
            baseShop: tracker.baseShop || null,
            createdAt: tracker.createdAt || null,
            createdAtMillis: toMillis(tracker.createdAt),
            notes: fourByFour.notes || [],

            // FIXED: Keep the full nested fourByFour object intact
            fourByFour: fourByFour,
            licensing: {}, // Initialize empty licensing data
          };
        };

        const applyRows = (nextRows, nextCursor) => {
          setRows(nextRows);
          nextRows.forEach((r) => {
            attachTrackerListener(r.id);
            attachLicensingListener(r.id);
          });
          pageStateRef.current.nextCursor = nextCursor || null;
        };

        if (hasSearchFilters) {
          const qy = query(
            collection(db, TRACKERS_COLLECTION),
            orderBy("fourByFour.createdAt", "desc")
          );

          const snap = await getDocs(qy);
          if (cancelled) return;

          const trackerDocs = snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            _doc: d,
          }));

          const baseRows = trackerDocs.map(mapTrackerToRow);
          applyRows(baseRows, null);
        } else {
          const pageSize = pageStateRef.current.pageSize;

          if (allowedUserIds.size === 0) {
            const qy = query(
              collection(db, TRACKERS_COLLECTION),
              orderBy("fourByFour.createdAt", "desc"),
              limit(pageSize)
            );

            const snap = await getDocs(qy);
            if (cancelled) return;

            const trackerDocs = snap.docs.map((d) => ({
              id: d.id,
              ...d.data(),
              _doc: d,
            }));

            const baseRows = trackerDocs.map(mapTrackerToRow);
            const last = snap.docs[snap.docs.length - 1] || null;
            applyRows(baseRows, last);
            return;
          }

          let cursor = null;
          let lastDoc = null;
          const seenIds = new Set();
          const collected = [];

          do {
            const qy = query(
              collection(db, TRACKERS_COLLECTION),
              orderBy("fourByFour.createdAt", "desc"),
              ...(cursor ? [startAfter(cursor)] : []),
              limit(pageSize)
            );

            const snap = await getDocs(qy);
            if (cancelled) return;

            const trackerDocs = snap.docs.map((d) => ({
              id: d.id,
              ...d.data(),
              _doc: d,
            }));

            const pageRows = trackerDocs.map(mapTrackerToRow);
            const allowedRows = pageRows.filter((r) =>
              allowedUserIds.has(r.id)
            );

            allowedRows.forEach((row) => {
              if (!seenIds.has(row.id)) {
                seenIds.add(row.id);
                collected.push(row);
              }
            });

            lastDoc = snap.docs[snap.docs.length - 1] || null;
            cursor = lastDoc;
          } while (collected.length < pageSize && lastDoc);

          applyRows(collected, lastDoc);
        }
      } catch (e) {
        console.error("Error loading trackers:", e);
        setError("Failed to load 4×4 users.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    function attachTrackerListener(userId) {
      if (trackerUnsubsRef.current.has(userId)) return; // already listening

      // ✅ OPTIMIZATION: Only listen to fourByFour changes, not entire document
      const unsub = onSnapshot(
        doc(db, TRACKERS_COLLECTION, userId),
        {
          // ✅ Only trigger on actual data changes (not metadata)
          includeMetadataChanges: false
        },
        (docSnapshot) => {
          if (!docSnapshot.exists()) return;

          const trackerData = docSnapshot.data();
          const fourByFour = trackerData.fourByFour || {};

          // ✅ OPTIMIZATION: Only update if data actually changed
          setRows((prev) => {
            const currentRow = prev.find(r => r.id === userId);
            if (!currentRow) return prev;

            // Check if fourByFour data is different
            const currentFourByFour = JSON.stringify(currentRow.fourByFour || {});
            const newFourByFour = JSON.stringify(fourByFour);

            if (currentFourByFour === newFourByFour) {
              return prev; // ✅ No change, skip update
            }

            // FIXED: Update with proper nested structure
            const updates = {
              // Root fields
              name: trackerData.name || displayName(trackerData),
              firstName: trackerData.firstName,
              lastName: trackerData.lastName,
              email: trackerData.email,
              phone: trackerData.phone,
              leader: trackerData.leader || "—",
              code: trackerData.code,
              date: trackerData.date,
              baseShop: trackerData.baseShop || null,

              // FIXED: Update the full fourByFour object, not flattened fields
              fourByFour: fourByFour,
              recruiter: fourByFour.recruiter || "—",
              notes: fourByFour.notes || [],
            };

            return prev.map((row) =>
              row.id === userId ? { ...row, ...updates } : row
            );
          });
        }
      );

      trackerUnsubsRef.current.set(userId, unsub);
    }

    loadInitial();

    return () => {
      cancelled = true;
      // cleanup all listeners
      for (const [, unsub] of trackerUnsubsRef.current)
        try {
          unsub();
        } catch { }
      trackerUnsubsRef.current.clear();

      for (const [, unsub] of licensingUnsubsRef.current)
        try {
          unsub();
        } catch { }
      licensingUnsubsRef.current.clear();
    };
  }, [attachLicensingListener, allowedUserIds, debouncedNameQ, debouncedRecruiterQ, debouncedLeaderQ]);

  // Load more (next page of trackers)
  const loadMore = useCallback(async () => {
    const cursor = pageStateRef.current.nextCursor;
    if (!cursor || loadingMore) return;
    
    setLoadingMore(true);
    setLoadingMore(true);
    try {
      const qy = query(
        collection(db, TRACKERS_COLLECTION),
        orderBy("fourByFour.createdAt", "desc"),
        startAfter(cursor),
        limit(pageStateRef.current.pageSize)
      );

      const snap = await getDocs(qy);
      const newTrackers = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        _doc: d,
      }));

      // FIXED: Preserve nested structure in loadMore too
      // ✅ Load ALL data - hierarchy filtering happens in filteredRows
      const moreRows = newTrackers
        .map((tracker) => {
          const fourByFour = tracker.fourByFour || {};

          return {
            id: tracker.id,
            name: tracker.name || displayName(tracker),
            firstName: tracker.firstName,
            lastName: tracker.lastName,
            email: tracker.email,
            phone: tracker.phone,
            photoURL: tracker.photoURL,
            leader: tracker.leader || "—",
            recruiter: fourByFour.recruiter || "—",
            code: tracker.code,
            date: tracker.date,
            photo: tracker.photoURL,
            baseShopId: tracker.baseShopId || null,
            baseShop: tracker.baseShop || null,
            createdAt: tracker.createdAt || null,
            createdAtMillis: toMillis(tracker.createdAt),
            notes: fourByFour.notes || [],

            // FIXED: Keep full nested structure
            fourByFour: fourByFour,
            licensing: {}, // Initialize empty licensing data
          };
        });

      setRows((prev) => {
        const ids = new Set(prev.map((x) => x.id));
        const deduped = [...prev, ...moreRows.filter((r) => !ids.has(r.id))];
        // attach listeners for new ones
        moreRows.forEach((r) => {
          if (!trackerUnsubsRef.current.has(r.id)) {
            const unsub = onSnapshot(
              doc(db, TRACKERS_COLLECTION, r.id),
              { includeMetadataChanges: false }, // ✅ Only trigger on data changes
              (docSnapshot) => {
                if (!docSnapshot.exists()) return;

                const trackerData = docSnapshot.data();
                const fourByFour = trackerData.fourByFour || {};

                // ✅ OPTIMIZATION: Only update if data actually changed
                setRows((prev) => {
                  const currentRow = prev.find(row => row.id === r.id);
                  if (!currentRow) return prev;

                  // Check if fourByFour data is different
                  const currentFourByFour = JSON.stringify(currentRow.fourByFour || {});
                  const newFourByFour = JSON.stringify(fourByFour);

                  if (currentFourByFour === newFourByFour) {
                    return prev; // ✅ No change, skip update
                  }

                  // FIXED: Update with proper nested structure
                  const updates = {
                    name: trackerData.name || displayName(trackerData),
                    firstName: trackerData.firstName,
                    lastName: trackerData.lastName,
                    email: trackerData.email,
                    phone: trackerData.phone,
                    leader: trackerData.leader || "—",
                    code: trackerData.code,
                    date: trackerData.date,
                    baseShop: trackerData.baseShop || null,
                    fourByFour: fourByFour,
                    recruiter: fourByFour.recruiter || "—",
                    notes: fourByFour.notes || [],
                  };

                  return prev.map((row) =>
                    row.id === r.id ? { ...row, ...updates } : row
                  );
                });
              }
            );
            trackerUnsubsRef.current.set(r.id, unsub);
          }
          if (!licensingUnsubsRef.current.has(r.id)) {
            attachLicensingListener(r.id);
          }
        });
        return deduped;
      });

      const last = snap.docs[snap.docs.length - 1] || null;
      pageStateRef.current.nextCursor = last || null;
    } catch (e) {
      console.error("Error loading more trackers:", e);
      setError("Failed to load more users.");
    } finally {
      setLoadingMore(false);
    }
  }, [attachLicensingListener, allowedUserIds]);

  // Profile card data
  useEffect(() => {
    if (!profileId) return setProfileData(null);
    const row = rows.find((r) => r.id === profileId);
    if (!row) return setProfileData(null);
    setProfileData({
      photoUrl: row.photoURL,
      name: displayName(row),
      email: row.email,
      phone: row.phone,
      recruiter: row.recruiter,
      leader: row.leader,
      agencyCode: row.code,
      amaDate: row.date,
      notes: row.notes,
    });
  }, [profileId, rows]);

  // Subscribe to all notes for all trackers (4x4) using prospectId (returns all notes)
  useEffect(() => {
    if (rows.length === 0) return;

    const emailUnsubscribers = rows.map(row => {
      if (!row.id) return () => { };

      return subscribeToProspectNotes(row.id, (notes) => {
        setNotesByEmail(prev => ({
          ...prev,
          [row.id]: notes
        }));
      });
    });

    return () => {
      emailUnsubscribers.forEach(unsub => unsub());
    };
  }, [rows.length]);

  const getSortableValue = (r, key) => {
    if (key === "name") return displayName(r).toLowerCase();
    if (key === "createdAt") return r.createdAtMillis || 0;

    if (key.includes(".")) {
      return String(getNestedValue(r, key, "")).toLowerCase();
    }

    return (r[key] || "").toString().toLowerCase();
  };

  /* ---------- Filters ---------- */
  const filteredRows = useMemo(() => {
    let filtered = rows;

    const lq = debouncedLeaderQ.trim().toLowerCase();
    const rq = debouncedRecruiterQ.trim().toLowerCase();
    const nq = debouncedNameQ.trim().toLowerCase();
    const hasSearchFilters = !!(lq || rq || nq);

    // ✅ Apply hierarchy filter ONLY when no search filters are active
    if (!hasSearchFilters) {
      filtered = filtered.filter((r) => allowedUserIds.has(r.id));
    }

    // Apply search filters (shows ALL matching results, bypassing hierarchy)
    if (hasSearchFilters) {
      filtered = filtered.filter(
        (r) =>
          (lq ? (r.leader || "").toLowerCase().includes(lq) : true) &&
          (rq ? (r.recruiter || "").toLowerCase().includes(rq) : true) &&
          (nq ? displayName(r).toLowerCase().includes(nq) : true)
      );
    }

    if (modernTrackersEnabled) {
      if (dateRange?.start && dateRange?.end) {
        filtered = filtered.filter((item) => {
          const itemMs = toMillis(item.date || item.createdAt);
          return (
            itemMs >= new Date(dateRange.start).getTime() &&
            itemMs <= new Date(dateRange.end).getTime()
          );
        });
      }
      if (baseShopFilter !== "all") {
        filtered = filterByBaseShop(filtered, baseShopFilter);
      }
    }

    return filtered;
  }, [
    rows,
    debouncedLeaderQ,
    debouncedRecruiterQ,
    debouncedNameQ,
    modernTrackersEnabled,
    dateRange,
    baseShopFilter,
    allowedUserIds,
  ]);

  const sortedRows = useMemo(() => {
    const a = [...filteredRows];
    a.sort((x, y) => {
      const va = getSortableValue(x, sortKey);
      const vb = getSortableValue(y, sortKey);
      if (typeof va === "number" && typeof vb === "number")
        return sortDir === "asc" ? va - vb : vb - va;
      return (
        (sortDir === "asc" ? 1 : -1) * String(va).localeCompare(String(vb))
      );
    });
    return a;
  }, [filteredRows, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "createdAt" ? "desc" : "asc");
    }
  };

  const setRowLocal = (rid, updater) =>
    setRows((prev) => prev.map((r) => (r.id === rid ? updater({ ...r }) : r)));

  /* ---------- Update date field (for licensing dates) ---------- */
  const updateDateField = useCallback(async (row, col, newDate) => {
    const rid = row.id;
    const key = col.key;
    const linkedBoolean = col.linkedBoolean;

    // Update local state optimistically
    setRowLocal(rid, (r) => {
      let newRow = setNestedValue(r, key, newDate);

      // Update the linked boolean field
      if (linkedBoolean) {
        newRow = setNestedValue(newRow, linkedBoolean, !!newDate);
      }

      return newRow;
    });

    try {
      // Update the licensing document
      if (key.startsWith("licensing.")) {
        const fieldName = key.split(".")[1]; // testResultDate or sirconNiprDate
        await updateDoc(doc(db, LICENSING_COLLECTION, rid), {
          [fieldName]: newDate || null,
          updatedAt: serverTimestamp(),
        });
      }

      // Update the linked boolean in 4x4 tracker
      if (linkedBoolean) {
        await patchSingleField(
          rid,
          linkedBoolean,
          !!newDate,
          SOURCE.FOUR_BY_FOUR
        );
      }
    } catch (e) {
      console.error("Error updating date field:", e);
      // Revert local state on error
      const currentValue = getNestedValue(row, key);
      setRowLocal(rid, (r) => setNestedValue(r, key, currentValue));
    }
  }, []);

  /* ---------- Toggle check using the safe sync service ---------- */
  const toggleCheck = useCallback(async (row, col, isSavings = false) => {
    const rid = row.id;
    const key = col.key;

    // Handle read-only fields (don't allow toggle)
    if (col.readOnly) {
      console.log(`Field ${key} is read-only, cannot toggle`);
      return;
    }

    // If it's a savings cell and currently unchecked, focus the amount input first
    if (isSavings && !getNestedValue(row, key, false)) {
      setSavingsInputActive({ userId: rid, savingsKey: col.savingsKey });
      return;
    }

    const currentValue = getNestedValue(row, key, false);
    const nextVal = !currentValue;

    // Update local state optimistically
    setRowLocal(rid, (r) => {
      let newRow = setNestedValue(r, key, nextVal);

      // If unchecking a savings toggle, clear its amount locally
      if (isSavings && !nextVal && col.amountKey) {
        newRow = setNestedValue(newRow, col.amountKey, null);
      }

      return newRow;
    });

    try {
      // Use the safe service that triggers all cross-tracker rules
      await toggleFrom4x4(rid, key, nextVal);

      // Clear specific savings amount in Firestore when unchecking
      if (isSavings && !nextVal && col.amountKey) {
        await patchSingleField(rid, col.amountKey, null, SOURCE.FOUR_BY_FOUR);
      }
    } catch (e) {
      console.error("Error toggling check:", e);
      // revert local state on error
      setRowLocal(rid, (r) => setNestedValue(r, key, currentValue));
    }
  }, []);

  // Savings amount input (per-savings)
  const updateSavingsAmount = useCallback(
    async (row, savingsKey, value, amountKey) => {
      const rid = row.id;
      const parsed = value === "" ? null : Number(value);
      const shouldCheck = parsed !== null && parsed > 0;

      // Local update
      setRowLocal(rid, (r) => {
        let newRow = setNestedValue(r, amountKey, parsed);

        // auto-check the savings toggle if amount > 0
        if (shouldCheck) {
          newRow = setNestedValue(newRow, savingsKey, true);
        }
        return newRow;
      });

      try {
        // ✅ Use safe single field update for amount
        await patchSingleField(rid, amountKey, parsed, SOURCE.FOUR_BY_FOUR);

        // If amount > 0, ensure toggle is set true (and triggers fire)
        if (shouldCheck) {
          // ✅ Use the safe service for the toggle
          await toggleFrom4x4(rid, savingsKey, true);
          setSavingsInputActive(null);
        }
      } catch (e) {
        console.error("Error updating savings:", e);
      }
    },
    []
  );

  // ✅ Safe notes update using the service
  const updateNotes = useCallback(async (userId, newNotes) => {
    try {
      await ensureFourByFourExists(userId);
      // ✅ Use safe single field update for notes
      await patchSingleField(
        userId,
        "fourByFour.notes",
        newNotes,
        SOURCE.FOUR_BY_FOUR
      );
    } catch (e) {
      console.error("Error updating notes:", e);
      throw e;
    }
  }, []);

  // Add inline note function
  const addInlineNote = async (row) => {
    const text = (noteDrafts[row.id] || "").trim();
    if (!text) return;
    try {
      const recruiterName = auth?.currentUser?.displayName || auth?.currentUser?.email || "Me";

      // Use centralized notes service
      await addNote({
        prospectId: row.id,
        trackerId: row.id,
        emailId: row.id,
        text: text,
        createdBy: recruiterName,
        tracker: "4x4",
      });

      // Clear the draft
      setNoteDrafts((d) => ({ ...d, [row.id]: "" }));
    } catch (e) {
      console.error("Error adding note:", e);
    }
  };

  // Delete note function
  const deleteNote = async (row, noteId) => {
    if (!row) return;
    setNoteDeletingId(noteId);
    try {
      // Use centralized notes service
      await deleteNoteService(noteId);
    } catch (e) {
      console.error("Error deleting note:", e);
    } finally {
      setNoteDeletingId(null);
    }
  };

  // Get current notes for modal - combines 4x4 notes + prospect notes
  const currentNotesForOpenRow = useMemo(() => {
    if (!notesOpenFor) return [];
    const allNotes = notesByEmail[notesOpenFor.id] || [];
    const deduped = Array.from(
      allNotes.reduce((map, n) => {
        if (n?.id) map.set(n.id, n);
        return map;
      }, new Map()).values()
    );
    deduped.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    return deduped;
  }, [notesOpenFor, notesByEmail]);

  const sortedModalNotes = useMemo(() => {
    const copy = [...currentNotesForOpenRow];
    copy.sort((a, b) => toMillis(a.createdAt) - toMillis(b.createdAt));
    return copy;
  }, [currentNotesForOpenRow]);

  /* ---------- Render cells ---------- */
  const renderDateCell = (row, col, rowIndex) => {
    const dateValue = getNestedValue(row, col.key, "");
    const formattedDate = dateValue ? formatDateShort(dateValue) : "";

    return (
      <div className="light-cell date-cell">
        <input
          type="date"
          value={dateValue || ""}
          onChange={(e) => updateDateField(row, col, e.target.value || null)}
          className="date-input-cell"
          title={formattedDate || `Set ${col.label} date`}
        />
        {formattedDate && (
          <div className="date-display" title={formattedDate}>
            {formattedDate}
          </div>
        )}
      </div>
    );
  };

  const renderCheckCell = (row, col, rowIndex) => {
    const checked = getNestedValue(row, col.key, false);
    const isSavings = !!col.savings;
    const isReadOnly = !!col.readOnly;
    const savingsKey = col.savingsKey || col.key;
    const amountKey = col.amountKey;
    const isActiveSavings =
      savingsInputActive?.userId === row.id &&
      savingsInputActive?.savingsKey === savingsKey;

    // Read the right amount per column
    const currentAmount =
      amountKey &&
      amountKey.split(".").reduce((o, k) => (o ? o[k] : undefined), row);

    const cd = getCountdownFromAma(row);
    let pillClass = "count-pill";
    if (cd.hasAma) {
      if (cd.daysLeft <= 0) pillClass += " due";
      else if (cd.daysLeft <= 10) pillClass += " warn";
    }
    const endDateTooltip = cd.endDateISO
      ? `30-day window ends: ${new Date(cd.endDateISO).toLocaleDateString()}`
      : "Missing AMA date";

    return (
      <div
        className={`light-cell ${checked || isActiveSavings ? "green" : "red"
          } ${isSavings ? "savings-cell" : ""} ${isReadOnly ? "readonly" : ""}`}
        onClick={() => !isReadOnly && toggleCheck(row, col, isSavings)}
        style={isReadOnly ? { cursor: "default", opacity: 0.9 } : {}}
      >
        {cd.hasAma && !checked && (
          <span className={pillClass} title={endDateTooltip}>
            {cd.label}
          </span>
        )}

        {!isSavings && (
          <input
            type="checkbox"
            checked={checked}
            readOnly
            className="traffic-checkbox"
          />
        )}

        {isSavings && (checked || isActiveSavings) ? (
          <div className="savings-input-container">
            <input
              type="number"
              className="savings-input"
              value={currentAmount ?? ""}
              placeholder="Amount $$"
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                updateSavingsAmount(row, savingsKey, e.target.value, amountKey)
              }
              onBlur={() => {
                if (!currentAmount || currentAmount <= 0)
                  setSavingsInputActive(null);
              }}
              autoFocus={isActiveSavings}
            />
          </div>
        ) : isSavings ? (
          <input
            type="checkbox"
            checked={checked}
            readOnly
            className="traffic-checkbox"
          />
        ) : null}
      </div>
    );
  };

  const renderCell = (row, col, rowIndex) => {
    switch (col.key) {
      case "index":
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              color: "#f3d67a",
            }}
          >
            {rowIndex + 1}
          </div>
        );

      case "name": {
        const hasPhoto = !!(row.photoURL || row.avatarUrl || row.photo);
        const ama = formatDateShort(row.date);
        const code = row.code || "";
        const sub = [ama, code].filter(Boolean).join(" • ");
        return (
          <div className="name-like-cell" style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", height: "100%" }}>
            <AvatarSquare
              row={row}
              size={32}
              onClick={() => openProfile(row.id)}
            />
            <div
              className="name-text-wrap"
              style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start" }}
            >
              <div
                className="name-line"
                style={{
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  lineHeight: 1.2,
                }}
              >
                {!hasPhoto && (
                  <span
                    className="name-star"
                    style={{ color: "#f3d67a", fontWeight: 900 }}
                  >
                    *
                  </span>
                )}
                <span>{displayName(row)}</span>
              </div>
              {!!sub && (
                <div
                  className="name-sub"
                  style={{ fontSize: 12, opacity: 0.8, lineHeight: 1.2 }}
                >
                  {sub}
                </div>
              )}
            </div>
          </div>
        );
      }

      case "recruiter":
      case "leader": {
        const text = row[col.key] || "—";
        return (
          <div
            className="cell-with-btn"
            style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", height: "100%" }}
          >
            <button
              type="button"
              className="profile-mini"
              title="Open profile"
              onClick={() => openProfile(row.id)}
              style={{
                width: 26,
                height: 26,
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid rgba(255,255,255,.18)",
                background: "rgba(255,255,255,.06)",
                cursor: "pointer",
                color: "#fff",
                fontSize: "12px",
              }}
            >
              👤
            </button>
            <div className="cell-text" style={{ display: "flex", alignItems: "center", height: "100%" }}>{text}</div>
          </div>
        );
      }

      case "fourByFour.notes":
      case "notesInline": {
        const rid = row.id;
        const allNotes = (notesByEmail[row.id] || []).slice().sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

        const lastNote =
          allNotes.length > 0
            ? allNotes
              .slice()
              .reverse()
              .find((n) => n.text)
            : null;

        const isNoteFocused = focusedNoteInputId === rid;

        // Show latest note in the input when not focused, or draft when focused/typing
        const inputValue = isNoteFocused
          ? (noteDrafts[rid] || "")
          : (noteDrafts[rid] || (lastNote?.text || ""));

        return (
          <div className="notes-cell-content">
            <div className="notes-inline-add">
              <input
                className={`note-input ${!isNoteFocused && lastNote && !noteDrafts[rid] ? "showing-existing" : ""}`}
                type="text"
                placeholder={lastNote ? "Add a new note... (Press Enter)" : "Add a quick note... (Press Enter)"}
                value={inputValue}
                onChange={(e) =>
                  setNoteDrafts((d) => ({ ...d, [rid]: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (noteDrafts[rid] || "").trim()) {
                    addInlineNote(row);
                  }
                }}
                onFocus={() => {
                  setFocusedNoteInputId(rid);
                  // Clear the input when user focuses to type new note
                  if (!noteDrafts[rid]) {
                    setNoteDrafts((d) => ({ ...d, [rid]: "" }));
                  }
                }}
                onBlur={() => setFocusedNoteInputId(null)}
              />
              <button
                className="notes-openall-emoji"
                onClick={() => setNotesOpenFor(row)}
                title="Open all notes"
                type="button"
              >
                ✏️
              </button>
            </div>

            {/* Show note metadata below when showing existing note */}
            {!isNoteFocused && lastNote && !noteDrafts[rid] && (
              <div className="note-meta-under">
                {lastNote.createdBy || "—"} • {
                  lastNote.createdAtISO
                    ? formatDateTimePretty(lastNote.createdAtISO)
                    : toMillis(lastNote.createdAt)
                      ? formatDateTimePretty(
                        new Date(toMillis(lastNote.createdAt)).toISOString()
                      )
                      : ""
                }
              </div>
            )}
          </div>
        );
      }

      default:
        if (col.type === "date") {
          return renderDateCell(row, col, rowIndex);
        }
        return renderCheckCell(row, col, rowIndex);
    }
  };

  /* ---------- Filters header ---------- */
  const filtersComponent = modernTrackersEnabled ? (
    <div className="tracker-filter-row">
      <div className="tracker-metric-card">
        <div className="metric-icon">
          <IconUsers size={20} stroke={1.7} />
        </div>
        <div className="metric-copy">
          <span className="metric-label">Loaded Users</span>
          <span className="metric-value">{rows.length}</span>
        </div>
      </div>
      <TrackerFilters
        users={rows}
        currentUser={null}
        onRangeChange={setDateRange}
        onBaseShopChange={setBaseShopFilter}
        defaultRange="all"
        defaultBaseShop="all"
        className="tracker-filter-controls"
        layout="inline"
      />
    </div>
  ) : null;

  const TrackerWrapper = modernTrackersEnabled
    ? TrackerContainer
    : TrackerShell;
  const wrapperProps = modernTrackersEnabled
    ? {
      title: "4×4 Tracker",
      trackerType: "4x4",
      totalCount: null,
      showFilters: true,
      filterComponent: filtersComponent,
      left: error && <span className="text-sm opacity-75">{error}</span>,
    }
    : {
      title: "4×4 Tracker",
      left: error && <span className="text-sm opacity-75">{error}</span>,
    };

  const gridProps = {
    columns: COLS,
    rows: sortedRows,
    loading,
    emptyText: "No 4x4 data found.",
    renderCell,
    rowKey: (r) => r.id,
    lockableLeftCount: 4,
    initialLockedKeys: ["index", "name", "recruiter", "leader"],
    sortKey,
    sortDir,
    onSort: handleSort,
    loadingMore,
    renderHeaderSearch: (col) => {
      if (col.key === "name")
        return (
          <input
            className="mini-search"
            placeholder="Search Name..."
            value={nameQ}
            onChange={(e) => setNameQ(e.target.value)}
          />
        );
      if (col.key === "recruiter")
        return (
          <input
            className="mini-search"
            placeholder="Search Recruiter..."
            value={recruiterQ}
            onChange={(e) => setRecruiterQ(e.target.value)}
          />
        );
      if (col.key === "leader")
        return (
          <input
            className="mini-search"
            placeholder="Search Leader..."
            value={leaderQ}
            onChange={(e) => setLeaderQ(e.target.value)}
          />
        );
      return null;
    },
  };

  return (
    <>
      <TrackerWrapper {...wrapperProps}>
        <ResizableGrid {...gridProps} />
        <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
          <button
            className="btn"
            onClick={loadMore}
            disabled={loadingMore || !pageStateRef.current.nextCursor}
          >
            {pageStateRef.current.nextCursor
              ? loadingMore
                ? "Loading…"
                : "Load more users"
              : "All loaded"}
          </button>
        </div>
      </TrackerWrapper>

      <UserProfileCard
        open={!!profileId && !!profileData}
        onClose={closeProfile}
        person={profileData || {}}
      />

      {/* Notes Modal */}
      {notesOpenFor && (
        <div className="modal-backdrop" onClick={() => setNotesOpenFor(null)}>
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <div className="modal-head">
              <h3>Notes — {displayName(notesOpenFor)}</h3>
              <button
                className="btn ghost"
                onClick={() => setNotesOpenFor(null)}
              >
                ✕
              </button>
            </div>
            <div className="notes-list-modal">
              {sortedModalNotes.length === 0 && (
                <div className="empty-notes">No notes yet.</div>
              )}
              {sortedModalNotes.map((n) => (
                <div key={n.id} className="note-row-modal">
                  <div className="note-text-modal">{n.text}</div>
                  <div className="note-meta-modal">
                    {n.createdBy || "—"} •{" "}
                    {n.createdAtISO
                      ? formatDateTimeISO(n.createdAtISO)
                      : toMillis(n.createdAt)
                        ? formatDateTimeISO(
                          new Date(toMillis(n.createdAt)).toISOString()
                        )
                        : ""}
                  </div>
                  <button
                    className="note-delete-modal"
                    onClick={() => deleteNote(notesOpenFor, n.id)}
                    disabled={noteDeletingId === n.id}
                    title="Delete note"
                  >
                    {noteDeletingId === n.id ? "…" : "Delete"}
                  </button>
                </div>
              ))}
            </div>
            <div className="notes-modal-add">
              <textarea
                className="input"
                rows="2"
                placeholder="Add a new note..."
                value={noteDrafts["modal"] || ""}
                onChange={(e) =>
                  setNoteDrafts((d) => ({ ...d, modal: e.target.value }))
                }
              ></textarea>
              <button
                className="btn primary"
                onClick={async () => {
                  const text = (noteDrafts["modal"] || "").trim();
                  if (!text) return;

                  const recruiterName = auth?.currentUser?.displayName || auth?.currentUser?.email || "Me";

                  try {
                    // Use centralized notes service
                    await addNote({
                      prospectId: notesOpenFor.id,
                      trackerId: notesOpenFor.id,
                      emailId: notesOpenFor.id,
                      text: text,
                      createdBy: recruiterName,
                      tracker: "4x4",
                    });

                    setNoteDrafts((d) => ({ ...d, modal: "" }));
                  } catch (e) {
                    console.error("Error adding note:", e);
                  }
                }}
              >
                Add Note
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .light-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          height: 100%;
          cursor: pointer;
        }

        .savings-cell {
          flex-direction: column;
          gap: 4px;
          padding: 4px;
        }

        .savings-input-container {
          display: flex;
          justify-content: center;
          width: 100%;
        }

        .savings-input {
          width: 86%;
          max-width: 120px;
          height: 28px;
          border-radius: 8px;
          padding: 0 8px;
          font-weight: 700;
          text-align: center;
          border: 1px solid #4a5568;
          background: rgba(255, 255, 255, 0.9);
          color: #1a202c;
        }

        .savings-input:focus {
          outline: none;
          border-color: #4299e1;
          background: white;
        }

        .count-pill {
          position: absolute;
          top: 2px;
          right: 2px;
          background: #4a5568;
          color: white;
          padding: 1px 4px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: bold;
          z-index: 1;
          margin-right: 6px;
        }

        .date-cell {
          flex-direction: column;
          gap: 4px;
          padding: 4px;
        }

        .date-input-cell {
          width: 100%;
          padding: 4px;
          border: 1px solid #4a5568;
          border-radius: 4px;
          background: #1a202c;
          color: #f7fafc;
          font-size: 12px;
        }

        .date-input-cell:focus {
          outline: none;
          border-color: #4299e1;
        }

        .date-display {
          font-size: 11px;
          color: #a0aec0;
          text-align: center;
        }

        .traffic-checkbox {
          display: none;
        }

        .light-cell.green {
          background: rgba(72, 187, 120, 0.2);
        }

        .light-cell.red {
          background: rgba(245, 101, 101, 0.2);
        }

        .light-cell.green .traffic-checkbox {
          display: block;
        }

        .light-cell.red .traffic-checkbox {
          display: block;
        }

        /* Notes Cell Styles */
        .notes-cell-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
          width: 100%;
          align-items: flex-start;
        }

        .notes-inline-add {
          display: flex;
          align-items: center;
          gap: 6px;
          width: 100%;
        }

        .note-input {
          flex: 1 1 auto;
          min-width: 120px;
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          padding: 6px 10px;
          transition: all 0.2s ease;
        }

        .note-input.showing-existing {
          background: rgba(255, 215, 0, 0.08);
          border-left: 3px solid rgba(255, 215, 0, 0.4);
          padding-left: 8px;
          font-style: italic;
          opacity: 0.95;
        }

        .notes-openall-emoji {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          padding: 6px 8px;
          cursor: pointer;
        }

        .note-meta-under {
          font-size: 11px;
          opacity: 0.8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Notes Modal Styles - ProspectTracker style */
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-card {
          background: #1f2128;
          color: #fff;
          border: 1px solid #3a3f4a;
          border-radius: 16px;
          box-shadow: 0 14px 40px rgba(0,0,0,.45);
          padding: 16px 16px 12px;
          max-height: 92vh;
          overflow: auto;
          width: min(860px,94vw);
        }
        
        .modal-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 8px;
        }
        
        .modal-body {
          display: block;
        }
        
        .btn {
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.18);
          color: #fff;
          border-radius: 10px;
          padding: 8px 12px;
          cursor: pointer;
        }
        
        .btn.primary {
          background: rgba(255,215,0,.2);
          color: #ffd700;
          border-color: rgba(255,215,0,.35);
        }
        
        .btn.ghost {
          background: transparent;
          border-color: rgba(255,255,255,.25);
        }
        
        .input {
          width: 100%;
          box-sizing: border-box;
          background: rgba(255,255,255,.06);
          color: #fff;
          border: 1px solid rgba(255,255,255,.15);
          border-radius: 10px;
          padding: 10px 12px;
          outline: none;
        }
        
        .input:focus {
          border-color: rgba(255,215,0,.55);
          box-shadow: 0 0 0 2px rgba(255,215,0,.12) inset;
        }
        
        .notes-list-modal {
          max-height: 40vh;
          overflow: auto;
          background: rgba(255,255,255,.04);
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 12px;
          padding: 8px;
          margin-bottom: 12px;
        }
        
        .empty-notes {
          opacity: .7;
          text-align: center;
          padding: 18px 8px;
        }
        
        .note-row-modal {
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: 10px;
          align-items: center;
          padding: 8px;
          border-radius: 10px;
        }
        
        .note-row-modal:not(:last-child) {
          border-bottom: 1px dashed rgba(255,255,255,.08);
        }
        
        .note-text-modal {
          white-space: pre-wrap;
        }
        
        .note-meta-modal {
          opacity: .8;
          font-size: 12px;
        }
        
        .note-delete-modal {
          background: transparent;
          border: 1px dashed rgba(255,100,100,.4);
          color: rgba(255,100,100,.9);
          border-radius: 8px;
          padding: 4px 8px;
          cursor: pointer;
        }
        
        .notes-modal-add {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
      `}</style>
    </>
  );
}
